import React, { useState } from 'react';
import { useAdminUsers } from '../helpers/useAdminApi';
import { AdminResetPasswordDialog } from '../components/AdminResetPasswordDialog';
import { AdminUserView } from '../endpoints/admin/users_GET.schema';
import { Button } from '../components/Button';
import { Skeleton } from '../components/Skeleton';
import { Shield, KeyRound, Search } from 'lucide-react';
import { Input } from '../components/Input';
import { formatDateOnly } from '../helpers/formatDateUTC';
import { safeInitial } from '../helpers/stringUtils';
import styles from './admin.users.module.css';

export default function AdminUsersPage() {
  const { data: users, isLoading, error } = useAdminUsers();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUserView | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleResetClick = (user: AdminUserView) => {
    setSelectedUser(user);
    setIsDialogOpen(true);
  };

  const filteredUsers = users?.filter((user) => {
    const query = searchQuery.toLowerCase();
    return (
      user.email.toLowerCase().includes(query) ||
      user.displayName.toLowerCase().includes(query) ||
      user.role.toLowerCase().includes(query)
    );
  });

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <Shield size={48} className={styles.errorIcon} />
        <h2>Access Error</h2>
        <p>{error instanceof Error ? error.message : 'Failed to load users'}</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>User Management</h1>
          <p className={styles.subtitle}>Manage user access and security settings.</p>
        </div>
        <div className={styles.searchWrapper}>
          <Search className={styles.searchIcon} size={16} />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Joined</th>
              <th className={styles.actionsHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td>
                    <div className={styles.userCell}>
                      <Skeleton className={styles.avatarSkeleton} />
                      <div className={styles.userInfo}>
                        <Skeleton style={{ width: '120px', height: '1rem' }} />
                        <Skeleton style={{ width: '180px', height: '0.8rem' }} />
                      </div>
                    </div>
                  </td>
                  <td>
                    <Skeleton style={{ width: '60px', height: '1.5rem' }} />
                  </td>
                  <td>
                    <Skeleton style={{ width: '100px', height: '1rem' }} />
                  </td>
                  <td>
                    <Skeleton style={{ width: '100px', height: '2rem' }} />
                  </td>
                </tr>
              ))
            ) : filteredUsers?.length === 0 ? (
              <tr>
                <td colSpan={4} className={styles.emptyState}>
                  No users found matching &quot;{searchQuery}&quot;
                </td>
              </tr>
            ) : (
              filteredUsers?.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className={styles.userCell}>
                      {user.avatarUrl ? (
                        <img
                          src={user.avatarUrl}
                          alt={user.displayName}
                          className={styles.avatarImage}
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className={styles.avatarPlaceholder}>
                          {safeInitial(user.displayName)}
                        </div>
                      )}
                      <div className={styles.userInfo}>
                        <span className={styles.displayName}>{user.displayName}</span>
                        <span className={styles.email}>{user.email}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`${styles.roleBadge} ${styles[user.role]}`}>{user.role}</span>
                  </td>
                  <td className={styles.dateCell}>{formatDateOnly(user.createdAt)}</td>
                  <td className={styles.actionsCell}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleResetClick(user)}
                      title="Reset Password"
                    >
                      <KeyRound size={14} />
                      Reset
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AdminResetPasswordDialog
        user={selectedUser}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </div>
  );
}
